import * as Components from "./components";
import { World, Entity } from "ecsy";
import { addComponent } from "./utils";
import { CreationReceipt } from "./messageTypes";
import { generateSyncId, resetSyncIDs, ArrayVec2 } from "./synchronization";
import { createGameElement, EntityParameters } from "./gameElement";
import { vec2 } from "@tlaukkan/tsm";
import { CompositionElement } from "./composition";
import { Rng } from "./rng";
import { entityTrace, getEntitySummary, printEntityTrace } from "./entityTrace";
import { ElementDisplayInfo } from "./elementDisplayInfo";
import { GameElement } from "./gameElementTypes";
import { Player } from "./player";
import { Game } from "./game";

const entitiesByID = new Map<number, Entity>();
const idsByEntity = new Map<Entity, number>();

let creationRequestBuffer = new Array<CreationRequest>();
let destroyRequestBuffer = new Array<Entity>();
let createdSyncEntitiesBuffer = new Array<CreationReceipt>();
let destroyedSyncEntitiesBuffer = new Array<{ id: number, type: GameElement }>();

let oneFrameCreationSet = new Set<number>();
let oneFrameDestructionSet = new Set<number>();

let emptyGarbageEntities = new Array<Entity>();

export type CreationRequest = {
	type: GameElement,
	position: ArrayVec2
	playerId: number,
	rotation: number,
	promoted?: boolean,
	id?: number,
	rngSeed?: number,
	unsynced?: boolean,
	sourceCompElement?: CompositionElement,
	previewOnly?: boolean,
	entityParameters?: EntityParameters
}

export function allEntities() {
	return Array.from(entitiesByID.values());
}

export function getEntityById(id: number) {
	return entitiesByID.get(id) || null;
}

export function getId(entity: Entity) {
	const id = idsByEntity.get(entity);
	return id === undefined ? null : id;
}

/** Create an entity (at the next appropriate point in the frame loop) */
export function createEntity(params: CreationRequest) {
	if (!params.unsynced) params.unsynced = false;
	if (params.id) {
		if (oneFrameCreationSet.has(params.id)) {
			return; //Don't accept two creation requests for the same entity on the same turn
		}
		oneFrameCreationSet.add(params.id)
	}
	creationRequestBuffer.push(params)
}


/** Create an entity right now, regardless of normal frame loop cycle*/
export function createEntityImmediate(world: World, params: CreationRequest) {
	if (!params.unsynced) params.unsynced = false;

	//ID source priority: 1.) Preserving source comp id, 2.) take id parameter, 3.) generate if we are server, 4.) Fail
	if (params.sourceCompElement) {
		params.id = params.sourceCompElement.id;
	}

	if (params.id === undefined && !params.previewOnly) {
		if (!process.env.is_server) {
			return null;
		}
		else {
			params.id = generateSyncId();
		}
	}

	//console.log(`Creating entity type(${GameElement[params.type]}) ${params.id} step ${getStepNumber()}`);

	params.rngSeed = params.rngSeed || Math.random();
	const rng = new Rng(params.rngSeed);

	const asset = ElementDisplayInfo.get(params.type);

	const gameElParams = { ...params, position: new vec2(params.position), rng, asset }
	const entity = createGameElement(world, gameElParams)

	if (params.playerId !== -1) {
		addComponent(entity, Components.Ownership, {
			playerId: params.playerId,
			sourceCompElement: params.sourceCompElement || null
		})
	}
	else {
		console.log('Creating entity with no player id');
	}

	if (!params.previewOnly) {
		entitiesByID.set(params.id!, entity);
		idsByEntity.set(entity, params.id!);
	}

	if (!params.unsynced && !params.previewOnly) {
		addComponent(entity, Components.Sync, { type: params.type, id: params.id, rng });

		if (process.env.is_server) { //not used on client so don't waste memory
			createdSyncEntitiesBuffer.push({
				type: params.type,
				playerId: params.playerId,
				position: params.position,
				rotation: params.rotation,
				rngSeed: params.rngSeed,
				id: params.id!,
				promoted: params.promoted,
				entityParameters: params.entityParameters
			});
		}
	}
	else if (params.previewOnly) {
		addComponent(entity, Components.PreviewOnly);
	}

	const type = params.type;
	entityTrace(entity, `created as type ${GameElement[type]} assigned SyncId: ` + params.id);
	return entity;
}

/**Barebones entity creation for simple proteins that should show up in shop and one round of sim */
export function dropShopProtein(game: Game, player: Player, dropperId: number, droppedId: number, type: GameElement, position: ArrayVec2) {
	//Add to local comp
	const desc = { type, position, id: droppedId, dropperId, roundDropped: game.getCurrentRound() };
	player.droppedProteins.push(desc);

	//Add to world
	createEntityImmediate(game.world, {
		type: type,
		position: position,
		id: droppedId,
		rotation: 0,
		playerId: player.id,
		unsynced: false,
	});
}

export function clearShopProteins(player: Player, dropperId = -1, beforeRound = Infinity) {
	if (dropperId === -1) {
		player.droppedProteins = [];
	}
	else {
		const entities = new Array<Entity>();
		player.droppedProteins = player.droppedProteins
			.filter(protein => {
				const rightDropper = protein.dropperId === dropperId;
				const expired = protein.roundDropped < beforeRound;
				const invalid = rightDropper && expired;
				if (invalid) {
					const entity = getEntityById(protein.id);
					if (entity) entities.push(entity);
				}
				return !invalid;
			});
		for (const entity of entities) {
			destroyEntity(entity);
		}
	}
}

export function cleanUpRemovedCellShopProteins(game: Game) {
	for (const player of game.players) {
		const checkedDroppers = new Set<number>();
		for (const protein of player.droppedProteins) {
			if (checkedDroppers.has(protein.dropperId)) continue;
			if (!game.getCompElementById(protein.dropperId)) {
				clearShopProteins(player, protein.dropperId);
			}
			checkedDroppers.add(protein.dropperId);
		}
	}
}

/** Destroy an entity (at the next appropriate point in the frame loop) */
export function destroyEntity(entity: Entity, allowClientDestroySync = false) {
	//Only server can destroy synced elements
	if (!allowClientDestroySync && entity.hasComponent(Components.Sync) && !process.env.is_server) {
		entityTrace(entity, "client destroy rejected");
		return;
	}

	//Commenting this check out because I think it hides more inisideous errors. It does work to reduce the number of double-destroy requests though.
	//const id = getId(entity);
	// if (id) {  
	// 	if (oneFrameDestructionSet.has(id)) {
	// 		return; //Don't accept two destruction requests for the same entity on the same turn
	// 	}
	// 	oneFrameDestructionSet.add(id)
	// }

	destroyRequestBuffer.push(entity);
	entityTrace(entity, "pushed to destroy buffer");
}

export function getDestroyBuffer() {
	return destroyRequestBuffer;
}

export function getCreateBuffer() {
	return destroyRequestBuffer;
}

/** Destroy an entity right now, regardless of normal frame loop cycle*/
export function destroyEntityImmediate(entity: Entity) {

	//NOTE: Ecsy has a bug where creating and removing an entity on the same frame leaves it still present in queries even though its invalid
	//To get around this, we store a list of entities that should be die and remove them at the top of the next frame.
	//https://github.com/MozillaReality/ecsy/issues/122

	if (!(entity as any).alive) {
		entityTrace(entity, "Attempt to destroy dead entity");
		return;
	};

	const sync = entity.getComponent(Components.Sync);
	if (sync && process.env.is_server) { //not used on client so don't waste memory
		destroyedSyncEntitiesBuffer.push({ id: sync.id, type: sync.type });
	}

	//console.log(`Destroying entity ${getEntitySummary(entity)} step ${getStepNumber()}}`);

	entityTrace(entity, "DestroyImmediate called");
	entity.removeAllComponents();
	const id = getId(entity);
	if (id !== null) entitiesByID.delete(id);
	idsByEntity.delete(entity);
	if (emptyGarbageEntities.includes(entity)) {
		console.log(`Adding repeat to garbage list`);
		printEntityTrace(entity);
	}
	emptyGarbageEntities.push(entity);
}

export function destroyAllEntities() {
	for (const entity of allEntities()) {
		//console.log(`Destroying entity ${getEntitySummary(entity)}`);
		destroyEntityImmediate(entity);
	}
	entitiesByID.clear();
	idsByEntity.clear();
	createdSyncEntitiesBuffer.splice(0, createdSyncEntitiesBuffer.length);
}

/** Handle queued up creation and destruction of entity requests */
export function processEntityCreationAndDestructionBuffers(world: World) {
	destroyRequestBuffer.forEach(entity => {
		destroyEntityImmediate(entity);
	});

	destroyRequestBuffer = [];
	creationRequestBuffer.forEach(request => {
		createEntityImmediate(world, request);
	});
	creationRequestBuffer = [];

	oneFrameCreationSet.clear();
	oneFrameDestructionSet.clear();
}

export function popCreatedEntitiesBuffer() {
	const result = [...createdSyncEntitiesBuffer];
	createdSyncEntitiesBuffer.splice(0, createdSyncEntitiesBuffer.length);
	return result;
}

export function popDestroyedEntitiesBuffer() {
	const result = [...destroyedSyncEntitiesBuffer];
	destroyedSyncEntitiesBuffer.splice(0, destroyedSyncEntitiesBuffer.length);
	return result;
}

export function clearEntityFactory() {
	destroyAllEntities();
	resetSyncIDs();
}

export function collectEntityGarbage() {
	emptyGarbageEntities.forEach(entity => entity.remove());
	emptyGarbageEntities = [];
}

